import React, { Component } from 'react';
import styled, { withTheme, DefaultTheme } from 'styled-components';
import Select from 'react-select';
import { OfferTemplateKind } from 'types/graphql/globalTypes';

import { ReactComponent as DropDownImg } from 'ressources/img/dropdown.svg';
import selectedIcon from 'ressources/img/selected.svg';


const SelectValue = styled.span`
    font-size: ${({ theme }) => theme.typography.fontSizeL}px;
    color: ${({ theme }) => theme.palette.text.dark};
    font-weight: ${({ theme }) => theme.typography.fontWeightLight};
    white-space: nowrap;
`;

const DropdownIndicator = () => {
    return <DropDownImg />;
};

const borderRadius = 14;
const padding = 20;
const boxShadow = '0 2px 2px 0 rgba(0,0,0,0.17)';
const getCustomStyles = (theme: DefaultTheme, style: any, isMulti: boolean) => {
    return {
        // In autoWidth mode the container shrink-wraps so the control can grow to its content.
        container: (provided: any) => ({
            ...provided,
            ...(style && style.autoWidth ? { display: 'inline-block', width: 'auto' } : {}),
        }),
        control: (provided: any) => ({
            ...provided,
            minHeight: style && style.height ? style.height : theme.height.select,
            ...(style && style.autoWidth
                ? { display: 'inline-flex', width: 'auto', minWidth: style.minWidth || theme.width.select }
                : { width: style && style.width ? style.width : theme.width.select }),
            paddingLeft: padding - 8,
            paddingRight: padding,
            border: 'none',
            borderRadius,
            boxShadow,
            ':hover': {
                borderColor: theme.palette.common.white,
                backgroundColor: theme.palette.common.white,
                borderRadius,
            },
        }),
        valueContainer:  (provided: any) => ({
            ...provided,
            overflowY: 'auto',
            maxHeight: style && style.height ? style.height : theme.height.select,
            padding: '8px',
            ...(style && style.autoWidth ? { flexWrap: 'nowrap' } : {}),
            ...((style && style.valueContainer) ?? {})
        }),
        singleValue: (provided: any) => ({
            ...provided,
            fontSize: theme.typography.fontSizeL,
            color: theme.palette.text.dark,
            fontWeight: theme.typography.fontWeightLight,
            fontFamily: theme.typography.fontFamily,
        }),
        input: (provided: any) => ({
            ...provided,
            ...(style && style.hideInput ? { display: 'none' } : {}),
        }),
        multiValue: (provided: any) => ({
            ...provided,
            fontSize: theme.typography.fontSizeL,
            color: theme.palette.text.dark,
            fontWeight: theme.typography.fontWeightLight,
            fontFamily: theme.typography.fontFamily,
            backgroundColor: theme.palette.background.light,
        }),
        clearIndicator: (prevStyle) => ({
            ...prevStyle,
            ':hover': { color: theme.palette.common.red },
            margin: '0 10px 0 0',
        }),
        option: (_: any, state: any) => {
            // Very light grey background to tell offer template versions apart while dev/testing
            const isNewOfferTemplate = state.data?.kind === OfferTemplateKind.OFFER_BUILDER;
            const newOfferTemplateTint = 'rgba(0, 0, 0, 0.01)';
            return {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding,
                borderRadius,
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.fontSizeL,
                color: theme.palette.text.dark,
                fontWeight: theme.typography.fontWeightLight,
                backgroundColor: isNewOfferTemplate ? newOfferTemplateTint : undefined,
                ':after': {
                    content: state.isSelected ? `url(${selectedIcon})` : '""',
                    display: 'block',
                },
                ':hover': {
                    fontWeight: theme.typography.fontWeightRegular,
                    backgroundColor: theme.palette.background.light,
                },
            };
        },
        menu: (provided: any) => ({
            ...provided,
            width: style && style.menuWidth ? style.menuWidth : style && style.width ? style.width : theme.width.select,
            boxShadow,
            borderRadius,
        }),
        // When the menu is portaled to the body it must sit above the rest of the page.
        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
        indicatorSeparator: () => ({}),
    };
};

export type OptionType = { value?: string; label?: string; secondaryLabel?: string; count?: number };

interface IProps {
    options: OptionType[];
    valuePrefix?: string;
    selectedOption?: OptionType | OptionType[];
    defaultValue?: OptionType | OptionType[];
    def?: OptionType | OptionType[];
    handleChange: (value: any, action: any) => void;
    theme: DefaultTheme;
    customLabel?: boolean;
    isMulti?: boolean;
    style?: any;
    isSearchable?: boolean;
    menuPlacement?: 'auto' | 'bottom' | 'top';
    menuPortal?: boolean;
    hideDropdownIndicator?: boolean;
}

class AppSelect extends Component<IProps> {
    render() {
        const { options, valuePrefix, selectedOption, handleChange, theme, customLabel, style, defaultValue, isMulti = false, isSearchable = true, menuPlacement = 'top', menuPortal = false, hideDropdownIndicator = false } = this.props;
        const customOptions = options.map((option) => ({
            ...option,
            label: `${option.secondaryLabel} (${option.count})`,
        }));
        return (
            <Select
                defaultValue={defaultValue}
                isSearchable={isSearchable}
                isMulti={isMulti}
                components={{
                    DropdownIndicator: hideDropdownIndicator ? () => null : DropdownIndicator,
                    SingleValue: ({ children }: any) => (
                        <>
                            <SelectValue style={style ? { fontSize: style.fontSize, margin: style.valueMargin } : undefined}>
                                {customLabel
                                    ? valuePrefix
                                        ? `${valuePrefix} ${children}`
                                        : selectedOption && customLabel
                                        // @ts-ignore
                                        ? `${selectedOption.secondaryLabel} (${selectedOption.count})`
                                        : `${children}`
                                    : valuePrefix
                                    ? `${valuePrefix} ${children}`
                                    : children}
                            </SelectValue>
                        </>
                    ),
                }}
                // @ts-ignore
                styles={getCustomStyles(theme, style, isMulti)}
                menuPlacement={menuPlacement}
                menuPortalTarget={menuPortal ? document.body : undefined}
                value={selectedOption}
                onChange={handleChange}
                options={customLabel ? customOptions : options}
            />
        );
    }
}

export default withTheme(AppSelect);
